import { parseDateToTimestamp } from "./dateHelpers";

interface Orderable {
  date: string;
  created_at: string;
  position?: number | null;
  sort_at?: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Una noticia está fijada cuando tiene `position` (1 = primera). */
export function isPinned(article: Orderable): boolean {
  return article.position != null;
}

/**
 * Clave de orden de una noticia no fijada (mayor = más arriba).
 * Si se movió desde el admin usa `sort_at`; si no, la fecha visible más la hora
 * de creación, que reproduce el orden por fecha y, dentro del día, por `created_at`.
 */
export function sortKey(article: Orderable): number {
  if (article.sort_at) {
    const ts = new Date(article.sort_at).getTime();
    if (!isNaN(ts)) return ts;
  }
  const day = parseDateToTimestamp(article.date);
  const created = new Date(article.created_at);
  const timeOfDay = isNaN(created.getTime())
    ? 0
    : ((created.getHours() * 60 + created.getMinutes()) * 60 + created.getSeconds()) * 1000 +
      created.getMilliseconds();
  return day + Math.min(timeOfDay, DAY_MS - 1);
}

/**
 * Orden del sitio: primero las noticias fijadas (por `position` ascendente),
 * después el resto por su clave de orden descendente y, si empatan, por `created_at`.
 */
export function compareArticles(a: Orderable, b: Orderable): number {
  const pa = a.position ?? null;
  const pb = b.position ?? null;
  if (pa !== null || pb !== null) {
    if (pa === null) return 1;
    if (pb === null) return -1;
    if (pa !== pb) return pa - pb;
  }
  const ka = sortKey(a);
  const kb = sortKey(b);
  if (kb !== ka) return kb - ka;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

/** Id de la última noticia subida (mayor `created_at`) de la lista. */
export function newestId<T extends Orderable & { id: number }>(list: T[]): number | undefined {
  let newest: T | undefined;
  for (const a of list) {
    if (!newest || new Date(a.created_at).getTime() > new Date(newest.created_at).getTime()) {
      newest = a;
    }
  }
  return newest?.id;
}

/**
 * Orden final del sitio: la última noticia subida siempre primero (incluso sobre
 * las fijadas) y después el orden de `compareArticles`.
 */
export function sortArticles<T extends Orderable & { id: number }>(
  list: T[],
  topId: number | undefined = newestId(list),
): T[] {
  const sorted = [...list].sort(compareArticles);
  const i = sorted.findIndex((a) => a.id === topId);
  if (i > 0) sorted.unshift(...sorted.splice(i, 1));
  return sorted;
}

/**
 * Clave para una noticia recién creada: queda por encima de todas las no fijadas,
 * aunque alguna se haya movido o tenga una clave "adelantada" (fecha visible de hoy
 * con hora de creación de la noche anterior).
 */
export function sortKeyForNew(existing: Orderable[]): number {
  const MINUTE = 60 * 1000;
  const top = existing
    .filter((a) => !isPinned(a))
    .reduce((max, a) => Math.max(max, sortKey(a)), 0);
  return Math.max(Date.now(), top + MINUTE);
}

/**
 * Nueva clave para colocar una noticia no fijada entre `above` y `below`
 * (vecinas en la lista ya ordenada; cualquiera puede faltar en los extremos).
 */
export function sortKeyBetween(above?: Orderable, below?: Orderable): number {
  const MINUTE = 60 * 1000;
  if (above && below) return Math.round((sortKey(above) + sortKey(below)) / 2);
  if (above) return sortKey(above) - MINUTE;
  if (below) return sortKey(below) + MINUTE;
  return Date.now();
}
